import React, { useState } from 'react';
import { Form, Button, Row, Col, Card, Alert } from 'react-bootstrap';
import { submitContactForm } from '../utils/emailService';
import '../styles/Contact.css';

const Contact = () => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  // Form validation state
  const [validated, setValidated] = useState(false);
  
  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    // Check form validity
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    setValidated(true);
    setIsSubmitting(true);
    
    try {
      // Submit the form data to the API
      await submitContactForm(formData);
      
      // Handle successful response
      setIsSubmitting(false);
      setShowSuccess(true);
      
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setValidated(false);
      
      // Hide success message after a few seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    } catch (error) {
      // Handle error
      setIsSubmitting(false);
      setErrorMessage(error.message || 'There was an error sending your message. Please try again.');
      setShowError(true);
    }
  };

  return (
    <div className="contact-page">
      <h2>Contact Us</h2>
      <p className="lead">
        Have questions about the Birmingham Go Club? Want to learn more about the game?
        Fill out the form below and we'll get back to you as soon as possible.
      </p>
      
      <Row>
        <Col lg={7} className="mb-4">
          {showSuccess && (
            <Alert variant="success" onClose={() => setShowSuccess(false)} dismissible>
              Your message has been sent successfully! We'll get back to you soon.
            </Alert>
          )}
          
          {showError && (
            <Alert variant="danger" onClose={() => setShowError(false)} dismissible>
              {errorMessage}
            </Alert>
          )}
          
          <Card className="contact-form-card">
            <Card.Body>
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="contactName">
                      <Form.Label>Your Name</Form.Label>
                      <Form.Control
                        required
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your name"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide your name.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="contactEmail">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        required
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide a valid email address.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-3" controlId="contactSubject">
                  <Form.Label>Subject</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="What is this regarding?"
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a subject for your message.
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3" controlId="contactMessage">
                  <Form.Label>Message</Form.Label>
                  <Form.Control
                    required
                    as="textarea"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Your message..."
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a message.
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={isSubmitting}
                  className="submit-button"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={5}>
          <Card className="contact-info-card">
            <Card.Body>
              <h3>Get In Touch</h3>
              
              <div className="contact-info-item">
                <h4>Email</h4>
                <p><a href="mailto:steelcitygoclub@gmail.com">steelcitygoclub@gmail.com</a></p>
              </div>
              
              <div className="contact-info-item">
                <h4>Phone</h4>
                <p><a href="tel:2059832422">(205) 983-2422</a></p>
              </div>
              
              <div className="contact-info-item">
                <h4>Social Media</h4>
                <div className="social-links">
                  <a href="https://discord.gg/kJMwtcXct3" className="social-link" target="_blank" rel="noopener noreferrer">Discord</a>
                </div>
              </div>

              <div className="contact-info-item">
                <h4>Meeting Schedule</h4>
                <p>For current meeting times, locations, and upcoming events, please visit our <a href="/events" style={{textDecoration: 'underline'}}>Events page</a>.</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Contact; 